import { useState } from "react";
import Modal from "components/elements/Dialog/Modal";
import IngressListItems from "./IngressListItems";
import IngressOverview from "./IngressOverview";
import IngressErrorPanel from "./IngressErrorPanel";
import { IngressListItemModel } from "./lib/IngressListItemModel";

export type FilterOption = "all" | "in-progress" | "complete";

type PanelState = "overview" | "error";

export default function IngressJobsModal({
  isOpen,
  onClose,
}: {
  readonly isOpen: boolean;
  readonly onClose: () => void;
}) {
  const [activeFilter, setActiveFilter] = useState<FilterOption>("all");
  const [selectedItem, setSelectedItem] = useState<IngressListItemModel | null>(
    null,
  );
  const [panelState, setPanelState] = useState<PanelState>("overview");

  const tabs = [
    { id: "all" as const, label: "All Imports" },
    { id: "in-progress" as const, label: "In Progress" },
    { id: "complete" as const, label: "Complete" },
  ];

  const handleShowError = (item: IngressListItemModel) => {
    setSelectedItem(item);
    setPanelState("error");
  };

  const handleBackToOverview = () => {
    setSelectedItem(null);
    setPanelState("overview");
  };

  const handleTabChange = (filterId: FilterOption) => {
    setActiveFilter(filterId);
    setSelectedItem(null); // Reset selected item when changing tabs
    setPanelState("overview");
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onEscape={onClose}
      disableSizing
      zIndex={30}
      className="h-[45rem]"
    >
      <div className="flex h-full flex-col px-4 py-4">
        <div className="m-4 w-full text-xl text-primary">Imports</div>

        {/* Tab Navigation */}
        <div className="mb-4 flex space-x-1 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={
                "px-4 py-2 text-sm font-medium transition-colors " +
                (activeFilter === tab.id
                  ? "border-primary border-b-2 text-primary"
                  : "text-gray-500 hover:text-gray-700")
              }
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="grid grow grid-cols-2 gap-4 divide-x overflow-hidden">
          <div className="flex min-h-0 flex-1 flex-col rounded-md bg-neutral-50 p-4">
            <IngressListItems
              filter={activeFilter}
              selectedExpandedItemId={
                panelState === "error" ? selectedItem?.id : null
              }
              onShowError={handleShowError}
              onClose={onClose}
            />
          </div>

          <div className="flex min-h-0 flex-1 flex-col pl-4">
            {panelState === "error" && selectedItem ? (
              <IngressErrorPanel
                item={selectedItem}
                onBack={handleBackToOverview}
              />
            ) : (
              <IngressOverview filter={activeFilter} />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
